"use client";

import { useEffect, useRef, useState } from "react";

type Props = {
  /** The short, display form — e.g. "96k+", "₹3 Cr+", "115k+". */
  display: string;
  /** The unrounded figure behind it — e.g. 96714. Also what the hover tooltip shows. */
  exact: number;
  /** Optional unit shown in front of the exact figure, e.g. "₹". */
  prefix?: string;
  className?: string;
  /**
   * The real number to count up from 0 to when this tile scrolls into view.
   * Deliberately separate from `exact`: a percentage tile passes `exact={0}`
   * to suppress its tooltip (see below) while still wanting a real number —
   * say, 75.4 — to animate toward.
   */
  target?: number;
  /** Renders `target` (and every value between 0 and it) the same way `display` was produced. */
  format?: (n: number) => string;
};

// Wraps an abbreviated stat so hovering (or keyboard-focusing) it reveals the
// exact figure in a small tooltip.
//
// The tooltip is suppressed when the display string already contains the exact
// digits — "341+" needs no tooltip saying "341". That makes this safe to use on
// every stat regardless of whether it happens to be abbreviated.
//
// When `target` + `format` are both supplied, the tile also counts up from 0
// the first time it scrolls into view, settling on the real `display` string
// rather than a recomputed one — `format(target)` should equal `display`, but
// snapping to the prop the caller already trusts avoids any chance of drift
// between the two.
export default function StatNumber({ display, exact, prefix = "", className, target, format }: Props) {
  // en-IN gives Indian digit grouping: 34685427 -> "3,46,85,427".
  const exactLabel = `${prefix}${exact.toLocaleString("en-IN")}`;
  const showTip = exact > 0 && !display.includes(String(exact));

  const canAnimate = target != null && target > 0 && !!format;

  // Starts at format(0) — identical on the server and on first client paint,
  // so there is no hydration mismatch and no flash of the real number before
  // it resets to zero and counts up. Same trick StatusSection's own counters
  // use. Once the count-up finishes this goes back to null, so the rendered
  // text becomes the caller's own `display` string again — the animation
  // hands control back rather than owning the final state.
  const [liveText, setLiveText] = useState<string | null>(canAnimate ? format!(0) : null);
  const ref = useRef<HTMLSpanElement>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!canAnimate || hasRun.current) return;
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduceMotion) {
      hasRun.current = true;
      setLiveText(null); // jump straight to the real `display`, no count-up
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting || hasRun.current) return;
        hasRun.current = true;
        io.disconnect();

        const duration = 2600;
        const start = performance.now();

        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3); // ease-out-cubic
          setLiveText(format!(target! * eased));
          if (t < 1) {
            requestAnimationFrame(tick);
          } else {
            setLiveText(null); // hand back to the caller's exact `display`
          }
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [canAnimate, target, format]);

  const text = liveText ?? display;

  // No tooltip needed → emit the bare text with no wrapper element at all,
  // unless the count-up needs a span to observe. Wrapping it in a <span> was
  // otherwise the one structural difference between tooltip'd and
  // non-tooltip'd stats, and it was enough to make them render differently
  // (e.g. "118+" in a fallback face while "115k+" beside it was correct).
  if (!showTip) {
    if (!canAnimate) {
      return className ? <span className={className} style={{ font: "inherit" }}>{display}</span> : <>{display}</>;
    }
    return (
      <span ref={ref} className={className} style={{ font: "inherit", fontVariantNumeric: "tabular-nums" }}>
        {text}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={`group relative cursor-pointer inline-block cursor-help ${className ?? ""}`}
      tabIndex={0}
      // aria-label, not title: `title` makes the browser draw its own native
      // tooltip on top of the styled one, so the number appeared twice.
      // aria-label keeps it announced to screen readers with no second box.
      aria-label={`${display} — exactly ${exactLabel}`}
      // Belt-and-braces: a wrapper component should never alter the typography
      // of the text it wraps, whatever the parent happens to set.
      style={{ font: "inherit", letterSpacing: "inherit", fontVariantNumeric: "tabular-nums" }}
    >
      {text}
      <span
        role="tooltip"
        className="pointer-events-none rounded-[8px] absolute bottom-full left-1/2 z-50 mb-2.5 -translate-x-1/2 translate-y-1 whitespace-nowrap  border border-[#2A3F6B] bg-[#0B1226] px-3 py-1.5 text-[13px] font-medium leading-none tracking-normal text-white opacity-0 shadow-[0_4px_16px_rgba(9,13,40,0.35)] transition-[opacity,transform] duration-150 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus:translate-y-0 group-focus:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100 motion-reduce:transition-none"
        style={{
          // The tooltip is UI chrome, not display type — pin it to the site's
          // body face at a fixed size so it looks the same on every stat block
          // instead of inheriting a 48px display font.
          fontFamily: "'Schibsted Grotesk', system-ui, sans-serif",
          fontVariantNumeric: "tabular-nums",
          fontStyle: "normal",
        }}
      >
        {exactLabel}
        {/* Arrow */}
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-full -ml-[5px] h-0 w-0 border-x-[5px] border-t-[5px] border-x-transparent border-t-[#0B1226]"
        />
      </span>
    </span>
  );
}
